import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Star, Gift, RotateCw, ZoomIn, Eye } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useMouseTracking } from "@/hooks/useMouseTracking";
import { SubtleWaves } from "@/components/ui/waves";
import { useProducts } from "@/hooks/useProducts";
import { useBundles } from "@/hooks/useBundles";
import { useSiteContent } from "@/hooks/useSiteContent";
import { products as fallbackProducts } from "@/data/products";
export const Products = () => {
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [productRotation, setProductRotation] = useState<Record<string, number>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [bundleHovered, setBundleHovered] = useState(false);
  const [fallbackActive, setFallbackActive] = useState(false);
  const { mousePosition } = useMouseTracking();
  const { addItem } = useCart();
  const { products, isLoading: productsLoading } = useProducts();
  const { bundles, isLoading: bundlesLoading } = useBundles();
  const { content } = useSiteContent('products');

  useEffect(() => {
    if (productsLoading && products.length === 0) {
      const t = setTimeout(() => setFallbackActive(true), 2000);
      return () => clearTimeout(t);
    } else {
      setFallbackActive(false);
    }
  }, [productsLoading, products.length]);

  // Merge fallback + backend by slug; keep fallback images when backend is missing/placeholder
  const isPlaceholder = (url?: string) =>
    !url || url.includes('placeholder') || url.includes('Geen afbeelding') || url.startsWith('data:image/svg+xml');

  const mergedProducts = fallbackProducts.map(fp => {
    const db = products.find(p => p.slug === fp.slug);
    if (!db) return fp;
    return {
      ...fp,
      name: db.name || fp.name,
      slug: fp.slug,
      tagline: db.tagline || fp.tagline,
      description: db.description || fp.description,
      longDescription: db.longDescription || fp.longDescription,
      price25g: db.price25g || fp.price25g,
      price50g: db.price50g || fp.price50g,
      bundlePrice: db.bundlePrice || fp.bundlePrice,
      originalPrice: db.originalPrice || fp.originalPrice,
      accentColor: (db as any).accentColor || fp.accentColor,
      borderColor: (db as any).borderColor || fp.borderColor,
      glowColor: (db as any).glowColor || fp.glowColor,
      icon: (db as any).icon || fp.icon,
      bullets: db.bullets?.length ? db.bullets : fp.bullets,
      ingredients: db.ingredients?.length ? db.ingredients : fp.ingredients,
      benefits: db.benefits?.length ? db.benefits : fp.benefits,
      productImage: isPlaceholder(db.productImage) ? fp.productImage : db.productImage,
      lifestyleImage: isPlaceholder(db.lifestyleImage) ? fp.lifestyleImage : db.lifestyleImage,
      detailImage: isPlaceholder(db.detailImage) ? fp.detailImage : db.detailImage,
      testimonials: db.testimonials?.length ? db.testimonials : fp.testimonials,
      isPopular: db.isPopular ?? fp.isPopular,
      isBestDeal: db.isBestDeal ?? fp.isBestDeal,
      isAccessory: db.isAccessory ?? fp.isAccessory,
      usage: db.usage || fp.usage,
      contentInfo: db.contentInfo || fp.contentInfo,
      seoDescription: db.seoDescription || fp.seoDescription,
      id: (db as any).id || fp.id,
    };
  });

  const displayProducts = mergedProducts.filter(p => !p.isAccessory).slice(0, 5);
  console.debug('[Products] counts', { products: products.length, merged: mergedProducts.length, display: displayProducts.length });
  
  // Diagnostische log voor producten zonder foto's
  if (displayProducts.length > 0) {
    const productsWithoutImages = displayProducts.filter(p => 
      !p.productImage || p.productImage.includes('placeholder') || p.productImage.includes('Geen afbeelding')
    );
    if (productsWithoutImages.length > 0) {
      console.warn('[Products] Producten zonder foto\'s:', productsWithoutImages.map(p => ({ slug: p.slug, image: p.productImage?.substring(0, 50) })));
    }
  }

  const activeBundle = bundles.find(b => b.isActive);

  const handleProductEnter = useCallback((id: string) => {
    setHoveredProduct(id);
  }, []);
  
  const handleProductLeave = useCallback(() => {
    setHoveredProduct(null);
    setIsDragging(false);
  }, []);
  
  const handleBundleEnter = useCallback(() => {
    setBundleHovered(true);
  }, []);
  
  const handleBundleLeave = useCallback(() => {
    setBundleHovered(false);
  }, []);

  const handleProductDrag = (productId: string, deltaX: number) => {
    if (isDragging) {
      setProductRotation(prev => ({
        ...prev,
        [productId]: ((prev[productId] || 0) + deltaX) % 360
      }));
    }
  };


  return (
    <section id="products" className="relative py-24 overflow-hidden bg-gradient-to-br from-emerald-50 via-background to-gold/10">
      {/* Elegant Wave Effects */}
      <SubtleWaves className="opacity-40" />
      
      {/* Lighter overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/30 via-transparent to-gold/20"></div>
      {/* Refined Falling Lotus Petals - Reduced for performance */}
      <div className="falling-petals">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="petal w-3 h-3 rounded-full opacity-15"
            style={{
              left: `${5 + i * 25}%`,
              background: `hsl(var(--gold-glow))`,
              animationDelay: `${i * 2}s`,
              animationDuration: `${15 + Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-gold text-glow mb-6 animate-on-scroll">
            {content?.title || 'Onze Premium Collectie'}
          </h2>
          
          <div className="w-16 h-1 bg-gradient-gold mx-auto mb-8 animate-on-scroll [animation-delay:0.2s] shadow-gold" />
          
          <p className="text-lg text-foreground/80 max-w-2xl mx-auto animate-on-scroll [animation-delay:0.4s]">
            {content?.description || 'Kies de perfecte hoeveelheid voor jouw spirituele reis'}
          </p>
        </div>

        {/* 3D PRODUCTS SHOWCASE REVOLUTION */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 max-w-6xl mx-auto mb-12 lg:mb-20">
          {displayProducts.map((product, index) => (
            <div
              key={product.slug}
              className="group perspective-2000 animate-on-scroll product-3d-container"
              style={{ animationDelay: `${index * 0.4}s` }}
              onMouseEnter={() => handleProductEnter(product.slug)}
              onMouseLeave={handleProductLeave}
            >
              {/* TRUE 3D FLOATING CARD - Desktop only */}
              <div 
                className="relative md:preserve-3d product-card-3d touch-manipulation"
                style={{
                  transform: hoveredProduct === product.slug 
                    ? `translateY(-10px) rotateX(${2 + mousePosition.y * 1}deg) rotateY(${mousePosition.x * 2}deg) translateZ(10px)` 
                    : 'translateY(0) rotateX(0) rotateY(0) translateZ(0)',
                  transition: 'all 0.6s cubic-bezier(0.23, 1, 0.320, 1)',
                  willChange: hoveredProduct === product.slug ? 'transform' : 'auto',
                  touchAction: 'manipulation',
                }}
              >
                {/* MULTI-LAYER CARD ARCHITECTURE */}
                <div className="relative bg-background rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-10 h-full preserve-3d">
                  {/* Background Shadow Layer */}
                  <div 
                    className="absolute inset-0 bg-gradient-gold rounded-3xl blur-2xl opacity-0 group-hover:opacity-20 transition-all duration-700 pointer-events-none"
                    style={{ 
                      transform: 'translateZ(-10px) scale(1.1)',
                    }}
                  />
                  
                  {/* Interactive Badge */}
                  {(product.isPopular || product.isBestDeal) && (
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-20">
                      <Badge className={`${product.isPopular ? 'bg-gradient-gold' : 'bg-primary'} text-black px-6 py-3 font-bold shadow-gold transform transition-all duration-300 hover:scale-110`}>
                        {product.isPopular ? 'Populair' : 'Beste Waarde'}
                      </Badge>
                    </div>
                  )}

                  {/* 360° PRODUCT CONFIGURATOR */}
                  <div className="relative mb-6 lg:mb-10 mt-4 lg:mt-6 product-configurator">
                    <div 
                      className="relative mx-auto w-40 h-40 sm:w-60 sm:h-60 lg:w-80 lg:h-80 md:cursor-grab md:active:cursor-grabbing"
                      style={{
                        transform: `
                          rotateY(${productRotation[product.slug] || 0}deg) 
                          scale(${hoveredProduct === product.slug ? 1.05 : 1})
                          translateZ(${hoveredProduct === product.slug ? 8 : 0}px)
                        `,
                        transition: isDragging ? 'none' : 'all 0.5s cubic-bezier(0.23, 1, 0.320, 1)',
                        touchAction: 'manipulation',
                      }}
                      onMouseDown={(e) => {
                        // Only enable drag on desktop
                        if (window.innerWidth < 768) return;
                        
                        setIsDragging(true);
                        const startX = e.clientX;
                        const handleMouseMove = (e: MouseEvent) => {
                          const deltaX = (e.clientX - startX) * 0.5;
                          handleProductDrag(product.slug, deltaX);
                        };
                        const handleMouseUp = () => {
                          setIsDragging(false);
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                    >
                      {/* Product Image with Depth */}
                      <img
                        src={product.productImage}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover object-center rounded-2xl shadow-floating preserve-3d"
                        style={{
                          filter: `brightness(${1.1 + mousePosition.y * 0.1}) contrast(${1.05 + mousePosition.x * 0.05})`,
                        }}
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.svg';
                        }}
                      />
                      
                      {/* Interactive Packaging Layers */}
                      <div className="absolute inset-0 packaging-overlay pointer-events-none">
                        <div className={`absolute inset-0 bg-gradient-gold-glow rounded-2xl transition-all duration-500 ${
                          hoveredProduct === product.slug ? 'opacity-25 blur-lg scale-110' : 'opacity-0'
                        }`} />
                        
                        {/* Size Indicators */}
                        <div className="absolute bottom-4 right-4 flex gap-2">
                          <div className="bg-black/80 text-gold px-3 py-1 rounded-full text-sm font-semibold">
                            25g / 50g
                          </div>
                        </div>
                      </div>

                      {/* Interactive Controls - Hidden on Mobile */}
                      <div className={`hidden md:flex absolute -bottom-4 left-1/2 transform -translate-x-1/2 gap-2 transition-all duration-300 ${
                        hoveredProduct === product.slug ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`}>
                        <button className="bg-gold/20 hover:bg-gold/40 text-gold p-3 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110 min-h-[44px] min-w-[44px]">
                          <RotateCw className="w-5 h-5" />
                        </button>
                        <button className="bg-gold/20 hover:bg-gold/40 text-gold p-3 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110 min-h-[44px] min-w-[44px]">
                          <ZoomIn className="w-5 h-5" />
                        </button>
                        <button className="bg-gold/20 hover:bg-gold/40 text-gold p-3 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110 min-h-[44px] min-w-[44px]">
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ENHANCED PRODUCT INFO */}
                  <div className="text-center space-y-4 lg:space-y-6 product-info-layer" style={{ transform: 'translateZ(5px)' }}>
                    <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-foreground group-hover:text-gold transition-colors duration-300">
                      {product.name}
                    </h3>
                    
                    <p className="text-foreground/80 leading-relaxed text-sm sm:text-base lg:text-lg">
                      {product.tagline || product.description}
                    </p>
                    
                    {/* Feature List */}
                    <div className="grid grid-cols-1 gap-2 text-sm text-foreground/70 mb-4">
                      {product.bullets?.slice(0, 3).map((bullet, idx) => (
                        <div key={idx} className="flex items-center justify-center gap-2">
                          <div className="w-1.5 h-1.5 bg-gold rounded-full"></div>
                          <span>{bullet}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* ANIMATED PRICING - Show both sizes */}
                    <div className="flex items-center justify-center gap-4 sm:gap-6 pricing-container">
                      <div className="text-center">
                        <div className="text-xs text-foreground/60 mb-1">25g</div>
                        <span className="text-xl sm:text-2xl font-bold text-gold animate-price-glow">
                          {product.price25g}
                        </span>
                      </div>
                      <div className="text-foreground/40">|</div>
                      <div className="text-center">
                        <div className="text-xs text-foreground/60 mb-1">50g</div>
                        <span className="text-xl sm:text-2xl font-bold text-gold animate-price-glow">
                          {product.price50g}
                        </span>
                      </div>
                    </div>
                    
                    {/* PROGRESSIVE STAR RATING */}
                    <div className="flex justify-center gap-1 mb-6">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-6 h-6 text-gold transition-all duration-300 star-fill-${i}`}
                          style={{
                            fill: hoveredProduct === product.slug ? 'currentColor' : i < 5 ? 'currentColor' : 'none',
                            animationDelay: `${i * 100}ms`,
                          }}
                        />
                      ))}
                      <span className="ml-3 text-foreground/60 font-medium">(127 reviews)</span>
                    </div>
                    
                    {/* ENHANCED CTA BUTTONS - 25g and 50g */}
                    <div className="grid grid-cols-2 gap-4 relative z-30">
                      <Button 
                        onClick={() => {
                          addItem({
                            id: `${product.slug}-25g`,
                            name: `${product.name} 25g`,
                            size: '25g',
                            price: parseFloat(product.price25g.replace('€', '').replace(',', '.')),
                            productId: String(product.id),
                            image: product.productImage
                          });
                        }}
                        className="btn-hero text-sm lg:text-base py-4 group relative overflow-hidden min-h-[56px] cursor-pointer"
                        style={{ touchAction: 'manipulation' }}
                      >
                        <span className="relative z-10 flex flex-col items-center gap-1">
                          <ShoppingCart className="w-4 h-4" />
                          <span className="text-xs">25g</span>
                        </span>
                      </Button>
                      <Button 
                        onClick={() => {
                          addItem({
                            id: `${product.slug}-50g`,
                            name: `${product.name} 50g`,
                            size: '50g',
                            price: parseFloat(product.price50g.replace('€', '').replace(',', '.')),
                            productId: String(product.id),
                            image: product.productImage
                          });
                        }}
                        className="btn-hero text-sm lg:text-base py-4 group relative overflow-hidden min-h-[56px] cursor-pointer"
                        style={{ touchAction: 'manipulation' }}
                      >
                        <span className="relative z-10 flex flex-col items-center gap-1">
                          <ShoppingCart className="w-4 h-4" />
                          <span className="text-xs">50g</span>
                        </span>
                      </Button>
                    </div>
                  </div>

                  {/* HOVER GLOW SYSTEM */}
                  <div className={`absolute inset-0 rounded-3xl transition-all duration-500 pointer-events-none ${
                    hoveredProduct === product.slug 
                      ? 'shadow-floating-intense border-2 border-gold/60' 
                      : 'border border-transparent'
                  }`} />
                  
                  {/* Sparkle Effects */}
                  {hoveredProduct === product.slug && (
                    <div className="sparkle-system absolute inset-0 pointer-events-none">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-2 h-2 bg-gold rounded-full animate-sparkle"
                          style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${i * 150}ms`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* BUNDLE VISUALIZATION EXPERIENCE */}
        {activeBundle && activeBundle.products && activeBundle.products.length >= 3 && (
          <div 
            className="max-w-5xl mx-auto animate-on-scroll bundle-experience"
            style={{ animationDelay: '1.2s' }}
            onMouseEnter={handleBundleEnter}
            onMouseLeave={handleBundleLeave}
          >
            <div className={`relative bg-primary/90 backdrop-blur-md rounded-2xl lg:rounded-3xl p-4 sm:p-8 lg:p-12 text-center border-2 transition-all duration-700 preserve-3d shadow-glow ${
              bundleHovered ? 'border-gold/60 shadow-floating-intense' : 'border-gold/30 elegant-glow'
            }`}>
              
              {/* Elegant Badge */}
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                <div className={`bg-gradient-gold text-black px-8 py-4 rounded-full font-bold shadow-gold flex items-center gap-3 md:transition-all md:duration-1500 ${
                  bundleHovered ? 'md:scale-103 md:shadow-floating' : ''
                }`}>
                  <Gift className={`w-6 h-6 md:transition-transform md:duration-1500 ${bundleHovered ? 'md:rotate-6 md:scale-105' : ''}`} />
                  <span className="text-lg">Speciale Aanbieding</span>
                </div>
              </div>
              
              {/* Dynamic Bundle Images */}
              <div className="mt-6 lg:mt-8 mb-6 lg:mb-8 bundle-morph-container">
                <div className={`flex justify-center items-center gap-2 sm:gap-4 lg:gap-8 md:transition-all md:duration-600 ${
                  bundleHovered ? 'md:gap-6' : ''
                }`}>
                  {activeBundle.products.map((product, idx) => (
                    <div key={`${product.name}-${idx}`}>
                      <div className={`transform md:transition-all md:duration-600 ${bundleHovered ? 'md:scale-103' : ''}`}>
                        <img 
                          src={product.productImage} 
                          alt={`${product.name} 25g`} 
                          loading="lazy" 
                          className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-lg lg:rounded-xl shadow-elegant object-cover" 
                          onError={(e) => e.currentTarget.src = '/assets/product-package.jpeg'}
                        />
                        <div className="text-xs sm:text-sm text-gold mt-1 lg:mt-2 font-semibold">25g</div>
                      </div>
                      {idx < activeBundle.products.length - 1 && (
                        <div className="text-gold text-xl sm:text-2xl lg:text-3xl font-bold inline-block mx-2">+</div>
                      )}
                    </div>
                  ))}
                  <div className="text-gold text-lg sm:text-xl lg:text-2xl hidden md:inline">→</div>
                  <div className={`relative md:transition-all md:duration-600 ${bundleHovered ? 'md:scale-105' : ''}`}>
                    <div className="w-20 h-12 sm:w-24 sm:h-16 lg:w-32 lg:h-20 bg-gradient-gold-glow rounded-lg lg:rounded-xl flex items-center justify-center relative overflow-hidden">
                      <span className="text-black font-bold text-sm sm:text-base lg:text-lg relative z-10">Bundle</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4 lg:space-y-6">
                <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-background">
                  {activeBundle.name}
                </h3>
                <p className="text-background/80 text-sm sm:text-base lg:text-lg leading-relaxed max-w-2xl mx-auto">
                  {activeBundle.description}
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pricing-showcase">
                  <div className="text-center">
                    <div className="text-sm text-background/60 mb-1">Normaal:</div>
                    <span className="text-xl sm:text-2xl text-background/60 line-through">
                      €{activeBundle.originalPrice?.toFixed(2)}
                    </span>
                  </div>
                  <div className="hidden sm:block text-4xl text-gold font-bold">→</div>
                  <div className="text-center">
                    <div className="text-sm text-gold/80 mb-1">Bundle Prijs:</div>
                    <span className={`text-3xl sm:text-4xl font-bold text-gold transition-all duration-300 ${bundleHovered ? 'animate-price-glow md:scale-110' : ''}`}>
                      €{activeBundle.bundlePrice?.toFixed(2)}
                    </span>
                  </div>
                  <Badge className={`bg-destructive text-white text-base sm:text-lg px-4 py-2 ${bundleHovered ? 'md:scale-105' : ''}`}>
                    -{activeBundle.discountPercentage}% Korting
                  </Badge>
                </div>
                
                <Button 
                  onClick={() => {
                    activeBundle.products?.forEach(product => {
                      addItem({
                        id: `${product.id}-25g-bundle`,
                        name: `${product.name} 25g`,
                        size: '25g',
                        price: parseFloat(product.price25g.replace('€', '').replace(',', '.')),
                        productId: String(product.id),
                        image: product.productImage
                      });
                    });
                  }}
                  className={`btn-hero text-base sm:text-lg px-6 sm:px-12 py-4 lg:py-6 relative group overflow-hidden min-h-[48px] w-full sm:w-auto ${bundleHovered ? 'md:scale-102' : ''}`}
                >
                  <span className="relative z-10 flex items-center gap-2 lg:gap-3 justify-center">
                    <ShoppingCart className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span>Bestel Complete Set</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-gold-glow opacity-0 group-hover:opacity-30 transition-opacity duration-800" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};